import { fabric } from 'fabric';
import { constrainToAxis, pointInRect } from '../utils/geometry';
import { DEFAULT_FONT_SIZE } from '../objects/EmbeddedTextBehaviorsV2';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type WhiteboardController from '../controllers/WhiteboardController';
import type Tool from '../tools/Tool';
import { hasRecordReference, isTextField } from '../objects/guards';
import type FrameMemberBehaviors from '../objects/FrameMemberBehaviors';
import { type MoveEvent } from '../external-types';
import { normalizeRecordType } from '../utils/record';

const oldIntializer = fabric.Object.prototype.initialize;
const oldRenderControls = fabric.Object.prototype._renderControls;
const oldDrawObject = fabric.Object.prototype.drawObject;
const oldDrawCacheOnCanvas = fabric.Object.prototype.drawCacheOnCanvas;

// this is just the subset that we currently use, since fabric doesn't provide a type
export interface StyleOverride {
  hasControls?: unknown;
  forActiveSelection?: unknown;
}

export type ToolList = Array<typeof Tool | ToolList>;

const ObjectBehaviors = {
  objectCaching: false,
  borderColor: '#0073CF',
  borderScaleFactor: 2,
  lockScalingFlip: true,
  cornerStyle: 'circle',
  transparentCorners: false,
  markedDeleted: false,
  lockedBorderColor: '#CDCDCD',
  unlockedBorderColor: '#0073CF',
  borderOpacity: 1,
  wasRendered: false,

  /**
   * Whether an object can use axis snapping when being moved.
   *
   * Triggered by moving an object while holding [canvas.snappedMovementKey]
   */
  snappedMovement: true,

  initialize(...args) {
    oldIntializer.apply(this, args);

    this.initializeUndoBehaviors();

    // Synthesize a moving:start event so we can do things at the beginning of
    // a drag operation.
    this.syntheticMoving = false;
    this.on('moving', eventData => {
      if (!this.syntheticMoving) {
        this.syntheticMoving = true;
        this._initSnappedMovement(eventData);
      } else {
        this._applySnappedMovement(eventData);
      }
      this.hasControls = false;
      this.updateGroup();
    });
    this.on('moved', () => {
      this.syntheticMoving = false;
      this.hasControls = true;
      this._snappedMovementOrigin = null;
      this._cleanupSnappedMovement();
      this.updateGroup();
    });

    this.on('scaling', () => {
      this.hasControls = false;
      this.updateGroup();
    });
    this.on('scaled', () => {
      this.hasControls = true;
      this.updateGroup();
    });
    this.on('rotating', () => {
      this.hasControls = false;
      this.updateGroup();
    });
    this.on('rotated', () => {
      this.hasControls = true;
      this.updateGroup();
    });

    // Let the controls responded to mouse over/out events.
    this.on('mouseover', eventData => {
      if (this.__corner && this.controls[this.__corner]?.onMouseOver) {
        this.hoveredCorner = this.__corner;
        this.controls[this.__corner].onMouseOver(eventData, this);
      }
    });
    const endHover = eventData => {
      if (this.hoveredCorner && this.controls[this.hoveredCorner]?.onMouseOut) {
        this.controls[this.hoveredCorner].onMouseOut(eventData, this);
      }
      this.hoveredCorner = null;
    };
    this.on('mouseout', endHover);

    const moveMouse = eventData => {
      // Mouse may go from one control to another without a mouseout event on
      // the shape
      if (this.hoveredCorner !== this.__corner) {
        endHover(eventData);
      }

      this.hoveredCorner = this.__corner;

      const func =
        eventData.e.type === 'mousemove' ? 'onMouseMove' : 'onMouseWheel';

      // If the shape contains the point, we cannot be over a control
      if (this.containsPoint(eventData.pointer)) {
        endHover(eventData);
      } else if (
        this.hoveredCorner &&
        this.controls[this.hoveredCorner]?.[func]
      ) {
        this.controls[this.hoveredCorner][func](eventData, this);
      }
    };
    this.on('mousemove', eventData => moveMouse(eventData));
    this.on('mousewheel', eventData => moveMouse(eventData));
    this.on('removed', endHover);
  },

  // If we moved an object inside a group, update the group.
  updateGroup() {
    // Only do this if an object inside a group is being manipulated.
    if (this.canvas?.getActiveObject() !== this) return;

    if (this.simpleGroup) {
      this.simpleGroup.syncFromObjects();
    }
  },

  _initSnappedMovement() {
    const center = this.getCenterPoint();
    this._snappedMovementOrigin = center;
    this._snappedMovementGuides = {};
  },

  _showSnapGuide(angle) {
    if (angle < 0) {
      angle += Math.PI;
    }
    const angleKey = String((angle / Math.PI) * 180);

    if (!this._snappedMovementGuides[angleKey]) {
      const viewPort = this.canvas.calcViewportBoundaries();
      const minSize = Math.max(
        viewPort.br.x - viewPort.bl.x,
        viewPort.br.y - viewPort.tr.y
      );
      const x = Math.cos(angle) * minSize;
      const y = Math.sin(angle) * minSize;
      const origin = this._snappedMovementOrigin;
      const ln = new fabric.Line(
        [origin.x - x, origin.y - y, origin.x + x, origin.y + y],
        {
          evented: false,
          stroke: '#4273fb',
          selectable: false,
          objectCaching: false,
          noScaleCache: false,
          opacity: 9,
          strokeWidth: 1,
        }
      );
      this._snappedMovementGuides[angleKey] = ln;
      this.canvas.add(ln);
    }

    const zoom = this.canvas.getZoom();
    for (const guideAngle in this._snappedMovementGuides) {
      const g = this._snappedMovementGuides[guideAngle];
      g.set({
        opacity: guideAngle === angleKey ? 1 : 0,
        strokeWidth: 1 / zoom,
      });
    }
  },

  _cleanupSnappedMovement() {
    this._snappedMovementOrigin = null;
    this._removeSnapGuides();
  },

  _removeSnapGuides() {
    for (const guideAngle in this._snappedMovementGuides) {
      this.canvas.remove(this._snappedMovementGuides[guideAngle]);
      delete this._snappedMovementGuides[guideAngle];
    }
    this.canvas?.requestRenderAll();
  },

  // When holding shift during object movement, snap the movement to defined axes
  _applySnappedMovement(eventData) {
    // Only the group shows guides, not each element in the group.
    if (this.simpleGroup) return;

    const key = this.canvas?.snappedMovementKey;
    if (
      !(
        this.snappedMovement &&
        this._snappedMovementOrigin &&
        eventData?.e?.[key]
      )
    ) {
      this._removeSnapGuides();
      return;
    }

    const SNAP_INTERVAL = Math.PI / 2;

    const origin = this._snappedMovementOrigin;
    const original = eventData.transform.original;
    const constrained = constrainToAxis(
      eventData.pointer,
      origin,
      SNAP_INTERVAL
    );

    this._showSnapGuide(
      Math.atan2(constrained.y - origin.y, constrained.x - origin.x)
    );

    const angle = constrained.angle;

    const object = eventData.transform.target;
    // Adjust for offset of pointer, which will be same as constrained point for
    // a given axis. This method is limited to 90 degree axes
    const { x, y } = object.getCenterPoint();
    const offsetXFromCenter = (constrained.x - x) * Math.abs(Math.cos(angle));
    const offsetYFromCenter = (constrained.y - y) * Math.abs(Math.sin(angle));

    object.set({
      left: constrained.x - origin.x + original.left - offsetXFromCenter,
      top: constrained.y - origin.y + original.top - offsetYFromCenter,
    });
  },

  _set(keyOrPath: KeyPath, value: any) {
    if (Array.isArray(keyOrPath)) {
      let obj = this;
      for (let i = 0; i < keyOrPath.length - 1; i++) {
        if (!(keyOrPath[i] in obj) || obj[keyOrPath[i]] === null) {
          obj[keyOrPath[i]] = {};
        } else if (typeof obj[keyOrPath[i]] !== 'object') {
          throw new Error(
            `Attempt to apply sub-property to non-object ${JSON.stringify(
              obj[keyOrPath[i]]
            )}`
          );
        }
        obj = obj[keyOrPath[i]];
      }
      // Fabric doesn't have any behaviors for sub-properties, so just set
      // the key ourselves.
      obj[keyOrPath[keyOrPath.length - 1]] = value;
    } else {
      // Call original fabric method to allow it to do its thing and set the
      // property.
      this.__original__set(keyOrPath, value);
    }

    this.canvas?.objectChanged(this);
  },

  firstObject() {
    return this;
  },

  // Quack like a group.
  setOnObjects(
    keyOrObject: string | Record<string, unknown>,
    value?: unknown,
    updateDefault = true
  ) {
    const fields =
      typeof keyOrObject === 'string' ? { [keyOrObject]: value } : keyOrObject;

    this._setObjectOnObjects(fields, updateDefault);
  },

  _setObjectOnObjects(
    fieldValues: Record<string, unknown>,
    updateDefault = true
  ) {
    for (const key in fieldValues) {
      if (updateDefault) {
        this.controller.defaultsManager.setFor(this, key, fieldValues[key]);
      }
      this.set(key, fieldValues[key]);
    }
  },

  getObjects(): fabric.Object[] {
    return [this];
  },

  forEach(func: (obj: fabric.Object) => void) {
    func(this);
  },

  // Exists so the sub-classes can add special handling for properties from the
  // remote.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setFromRemote(key: KeyPath, value: any, options?: Record<string, any>) {
    // If the position changed, update the coordinates.
    if (
      typeof key === 'string' &&
      ['left', 'top', 'width', 'height', 'scaleX', 'scaleY'].includes(key) &&
      this.canvas
    ) {
      this.setCoords();
      if (this.simpleGroup) {
        this.simpleGroup.syncFromObjects();
      }
    }
  },

  isSelected(this: fabric.Object): boolean {
    return !!this.canvas?.getActiveObjects().find(obj => obj === this);
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleted(controller: WhiteboardController) {},

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isDescendant(frame: FrameMemberBehaviors & fabric.Object) {
    return false;
  },

  positionConnectors() {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tools(topLevel?: boolean): ToolList | null {
    return null;
  },

  // Return position {left,top,width,height} of this object in canvas
  // coordinates regardless of grouping.
  absolutePosition() {
    const ownTransform = this.calcOwnMatrix();
    let left = this.left;
    let top = this.top;

    if (this.simpleGroup) {
      const point = new fabric.Point(-this.width / 2, -this.height / 2);
      const pointOnCanvas = fabric.util.transformPoint(point, ownTransform);

      left = pointOnCanvas.x;
      top = pointOnCanvas.y;
    } else if (isTextField(this)) {
      left = this.left + this.parent.left;
      top = this.top + this.parent.top;
    }

    const width = this.width * this.scaleX;
    const height = this.height * this.scaleY;

    return { left, top, width, height };
  },

  needsZoomEvents() {
    const fireZoom = () => this.fire('zoom');
    this.on('added', () => {
      this.canvas.on('zoom', fireZoom);
    });
    this.on('removed', () => {
      this.canvas.off('zoom', fireZoom);
    });
  },

  // Override in sub-classes to synchronize after object changes.
  synchronize(): void {},

  // subclasses should override to return any fixed object properties that can't be edited
  getControlledProperties(): Record<string, unknown> | undefined {
    return undefined;
  },

  fontSizeWithDefault() {
    return this.fontSize || DEFAULT_FONT_SIZE;
  },

  moveToCoords({ left, top }: { left?: number; top?: number }) {
    const transform = {
      original: fabric.util.saveObjectTransform(this),
      last: fabric.util.saveObjectTransform(this),
    };

    this.set({ left: left ?? this.left, top: top ?? this.top });
    this.fire('moving', { transform, frameMoving: true });
    transform.last = fabric.util.saveObjectTransform(this);
    this.fire('moved', { transform, frameMoving: true });
    this.setCoords();
  },

  drawObject(ctx: CanvasRenderingContext2D, forClipping: boolean) {
    this.wasRendered = true;
    oldDrawObject.apply(this, [ctx, forClipping]);
  },

  drawCacheOnCanvas(ctx: CanvasRenderingContext2D) {
    this.wasRendered = true;
    oldDrawCacheOnCanvas.apply(this, [ctx]);
  },

  // Check if the object, or its parent group, is moving.
  objectIsMoving(): boolean {
    return this.isMoving || this.simpleGroup?.objectIsMoving();
  },

  _renderControls(
    ctx: CanvasRenderingContext2D,
    styleOverride: StyleOverride,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    childrenOverride?: StyleOverride
  ) {
    // If an object inside a group is being manipulated, also show a faded
    // version of the group's border.
    if (this.simpleGroup) {
      const oldMoving = this.simpleGroup.isMoving;
      try {
        this.simpleGroup.isMoving = true;
        this.simpleGroup._renderControls(ctx, { hasControls: false });
      } finally {
        this.simpleGroup.isMoving = oldMoving;
      }
    }

    oldRenderControls.apply(this, [ctx, styleOverride]);
  },

  // While this is a bit of a shim, it has the advantage of avoiding changes to
  // fabric directly and diverging further from the upstream code.
  _renderStroke(ctx: CanvasRenderingContext2D) {
    const hasBorderOpacity = typeof this.borderOpacity === 'number';
    if (hasBorderOpacity) {
      ctx.save();
      ctx.globalAlpha = this.borderOpacity;
    }
    this.__original__renderStroke(ctx);
    if (hasBorderOpacity) {
      ctx.restore();
    }
  },

  // Render the exterior path of the object or the bounding box.
  renderPath(ctx: CanvasRenderingContext2D) {
    ctx.rect(-this.width / 2, -this.height / 2, this.width, this.height);
  },

  // Use a helper in case we want to fine tune based on object type
  isFrameLike() {
    return ['frame', 'device', 'presentationFrame'].includes(this.type);
  },

  hasExternalContent() {
    return false;
  },

  // Indicates whether the externalContent for this object (if any) should be
  // clipped in _renderObject. We clip the canvas to show externalContent, but
  // in certain circumstances (like exporting, for objects with placeholders),
  // we don't want to clip the content, as this leaves a blank spot on the export.
  clipExternalContent() {
    return true;
  },

  hasEmbeddedText() {
    return false;
  },

  textEditing: false,

  // Test if this object overlaps another
  overlapsObject(object: fabric.Object) {
    const { left, top } = this;
    const rect0 = {
      left,
      top,
      width: this.getScaledWidth(),
      height: this.getScaledHeight(),
    };
    const rect1 = {
      top: object.top,
      left: object.left,
      width: object.getScaledHeight(),
      height: object.getScaledWidth(),
    };

    const r0 = rect0.left + rect0.width;
    const b0 = rect0.top + rect0.height;
    const r1 = rect1.left + rect1.width;
    const b1 = rect1.top + rect1.height;

    const noOverlap =
      r0 <= rect1.left ||
      r1 <= rect0.left ||
      b0 <= rect1.top ||
      b1 <= rect0.top;

    return !noOverlap;
  },

  // Test if this object entirely contains another object
  containsObject(object: fabric.Object) {
    const { left, top } = this;
    const rect = {
      left,
      top,
      width: this.getScaledWidth(),
      height: this.getScaledHeight(),
    };
    const tl = new fabric.Point(object.left, object.top);
    const tr = new fabric.Point(
      object.left + object.getScaledWidth(),
      object.top
    );
    const bl = new fabric.Point(
      object.left,
      object.top + object.getScaledHeight()
    );
    const br = new fabric.Point(
      object.left + object.getScaledWidth(),
      object.top + object.getScaledHeight()
    );

    if (
      (pointInRect(tl, rect) || pointInRect(br, rect)) &&
      (pointInRect(bl, rect) || pointInRect(tr, rect))
    ) {
      const center = object.getCenterPoint();
      // For frame objects we want to use the fabric object's containsPoint()
      if (this.everContainsPoint) {
        return this.everContainsPoint(center, null, true, true);
      } else {
        return this.containsPoint(center, null, true, true);
      }
    }
  },

  recordReference() {
    if (!hasRecordReference(this)) {
      return null;
    }

    return {
      id: this.recordId,
      typename: normalizeRecordType(this.recordType),
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  styleFlagEnabled(key) {
    return true;
  },

  // Copied from fabric.js since it is not exported.
  scaleIsProportional(eventData) {
    const uniScaleKey = this.canvas.uniScaleKey;
    const uniformIsToggled = eventData[uniScaleKey];

    return uniformIsToggled || this.uniformScaling;
  },

  isHovered() {
    return this.canvas?._hoveredTarget === this;
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setHovered(e: MoveEvent): boolean {
    return true;
  },

  clearHovered() {},
};

export default ObjectBehaviors;
